import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Delete, Input, Field, Control, Label, Column } from "rbx";
import TextareaAutosize from "react-textarea-autosize";
import { DateInput, EmployeeSelect, ItemCategorySelect } from "../inputs";
import { formatCurrency, unmaskCurrency } from "../../utils";
import "./styles.scss";

function WorkOrderLaborItem({
  item,
  disabled = false,
  removeRow,
  handleChange,
  WorkOrder,
  Auth,
  companySettings
}) {
  const defaultDisabled = true;
  const LaborHourIncrement = companySettings?.LaborHourIncrement || 0.25;
  return (
    <React.Fragment>
      <Column.Group style={{ borderBottom: "solid 0.5px lightgray" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            marginTop: "2.5rem"
          }}
        >
          <Delete
            disabled={disabled}
            style={{ opacity: disabled ? 0 : 1 }}
            onClick={removeRow}
          />
        </div>
        <Column>
          <Field>
            <Control>
              <Label>Date Worked</Label>
              <DateInput
                disabled={disabled}
                name={`DateWorked_${item.RecId}`}
                value={
                  item.DateWorked
                    ? moment(item.DateWorked).format("YYYY-MM-DD")
                    : ""
                }
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control expanded>
              <EmployeeSelect
                required
                icon="user"
                label="Mechanic"
                name={`EmployeeRecIdRef_${item.RecId}`}
                readOnly={disabled}
                value={item.EmployeeRecIdRef}
                variables={{
                  where: { MechanicFlag: true },
                  orderByMulti: ["LastName_ASC"],
                  orderBy: "LastName_ASC"
                }}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control expanded>
              <ItemCategorySelect
                required
                icon="globe"
                label="Category"
                name={`ItemCategoryRecIdRef_${item.RecId}`}
                readOnly={disabled}
                value={item.ItemCategoryRecIdRef || WorkOrder.ItemCatgRecIdRef}
                where={{
                  ItemCatgTypeRecIdRef: WorkOrder.WOTypeRecIdRef
                }}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column size={2}>
          <Field>
            <Control expanded>
              <Label>Description</Label>
              <TextareaAutosize
                className="auto-height-textarea input"
                disabled={disabled}
                minRows={1}
                name={`Description_${item.RecId}`}
                type="text"
                value={item.Description}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column size={1}>
          <Field class="without-arrows">
            <Control expanded>
              <Label>Hours Worked</Label>
              <Input
                required
                disabled={disabled}
                name={`HoursWorked_${item.RecId}`}
                step={LaborHourIncrement}
                style={{ textAlign: "right" }}
                type="number"
                value={item.HoursWorked}
                onBlur={e => {
                  const { value } = e.target;
                  const result =
                    Math.ceil(parseFloat(value / LaborHourIncrement)) *
                    LaborHourIncrement;
                  handleChange({
                    target: { name: e.target.name, value: result }
                  });
                }}
                onChange={handleChange}
              />
            </Control>
            {WorkOrder.Billable &&
              WorkOrder.Billable === "Y" &&
              companySettings.AllowMaintenanceBilling && (
                <Control expanded style={{ marginTop: 5 }}>
                  <Label>Hours Billed</Label>
                  <Input
                    required
                    disabled={disabled}
                    name={`BillHours_${item.RecId}`}
                    step={LaborHourIncrement}
                    style={{ textAlign: "right" }}
                    type="number"
                    value={item.BillHours}
                    onBlur={e => {
                      const { value } = e.target;
                      const result =
                        Math.ceil(parseFloat(value / LaborHourIncrement)) *
                        LaborHourIncrement;
                      handleChange({
                        target: { name: e.target.name, value: result }
                      });
                    }}
                    onChange={handleChange}
                  />
                </Control>
              )}
          </Field>
        </Column>
        {Auth && (
          <React.Fragment>
            <Column size={1}>
              <Field>
                <Control expanded>
                  <Label>Cost</Label>
                  <Input
                    required
                    disabled={disabled}
                    name={`LaborCostRate_${item.RecId}`}
                    style={{ textAlign: "right" }}
                    type="text"
                    value={item.LaborCostRate}
                    onChange={handleChange}
                  />
                </Control>
                {WorkOrder.Billable &&
                  WorkOrder.Billable === "Y" &&
                  companySettings.AllowMaintenanceBilling && (
                    <Control expanded style={{ marginTop: 5 }}>
                      <Label>Price</Label>
                      <Input
                        required
                        disabled={disabled}
                        name={`BillingRate_${item.RecId}`}
                        style={{ textAlign: "right" }}
                        type="text"
                        value={item.BillingRate}
                        onChange={handleChange}
                      />
                    </Control>
                  )}
              </Field>
            </Column>
            <Column size={1}>
              <Field>
                <Control expanded>
                  <Label>Total</Label>
                  <Input
                    disabled={defaultDisabled}
                    name={`CostTotal_${item.RecId}`}
                    style={{ textAlign: "right" }}
                    type="text"
                    value={formatCurrency(
                      item.HoursWorked * unmaskCurrency(item.LaborCostRate)
                    )}
                  />
                </Control>
                {WorkOrder.Billable &&
                  WorkOrder.Billable === "Y" &&
                  companySettings.AllowMaintenanceBilling && (
                    <Control expanded style={{ marginTop: 5 }}>
                      <Label>Bill Total</Label>
                      <Input
                        disabled={defaultDisabled}
                        name={`BillTotal_${item.RecId}`}
                        style={{ textAlign: "right" }}
                        type="text"
                        value={formatCurrency(
                          item.BillHours * unmaskCurrency(item.BillingRate)
                        )}
                      />
                    </Control>
                  )}
              </Field>
            </Column>
            <Column>
              <Field>
                <Control expanded>
                  <Label>Note</Label>
                  <TextareaAutosize
                    className="auto-height-textarea input"
                    disabled={disabled}
                    minRows={1}
                    name={`LineNote_${item.RecId}`}
                    type="text"
                    value={item.LineNote}
                    onChange={handleChange}
                  />
                </Control>
              </Field>
            </Column>
          </React.Fragment>
        )}
      </Column.Group>
    </React.Fragment>
  );
}

WorkOrderLaborItem.propTypes = {
  item: PropTypes.object,
  disabled: PropTypes.bool,
  removeRow: PropTypes.func,
  handleChange: PropTypes.func,
  WorkOrder: PropTypes.object,
  Auth: PropTypes.bool,
  companySettings: PropTypes.object
};
export default WorkOrderLaborItem;
